import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import Loader from 'react-loader-spinner';
import MobileModal from './mobile_modal';
import RoundsModalContainer from '../containers/rounds_modal_container';
import EventsListModalContainer from '../containers/events_list_modal_container';
import { roundsFailedMessage } from '../utils/round_label_utils';
import { VIEW_MODES } from '../constants';

// The mobile/landscape picker sheet: the round list (multi-round pages) with
// each round's events, or the current round's events alone. Opening it
// refreshes both lists - rounds that started or had results released after
// page load only become selectable through this refetch (the mobile app has
// no page reload) - and, like the rounds modal's per-round previews, the
// sheet shows a spinner until they land and an error message when the round
// list fails, so it never shows a possibly stale list. That request state is
// the picker's own, not the store's.
const MobilePicker = forwardRef(function MobilePicker({
  viewMode,
  isManagerSite,
  showRoundsModal,
  fetchEvents,
  fetchRounds,
  handleClickEvent,
  handleClickSummary,
  handleClickEmptyRound,
}, ref) {
  const dialogRef = useRef(null);

  // Bumped on every open: the <dialog> stays mounted while closed, so
  // remounting the rounds modal resets it to the committed round expanded.
  const [session, setSession] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Each open owns the refresh it started: a result landing after a newer
  // open (or an unmount) is ignored.
  const openGenerationRef = useRef(0);
  useEffect(() => () => { openGenerationRef.current += 1; }, []);

  const open = useCallback(async () => {
    const generation = ++openGenerationRef.current;

    setSession((current) => current + 1);
    setError(false);
    setLoading(true);
    dialogRef.current?.open();

    const [, roundsResult] = await Promise.all([fetchEvents(viewMode), fetchRounds()]);

    if (generation !== openGenerationRef.current) return;
    setLoading(false);
    setError(roundsResult?.status === 'error');
  }, [fetchEvents, fetchRounds, viewMode]);

  useImperativeHandle(ref, () => ({
    open,
    close() {
      dialogRef.current?.close();
    },
  }), [open]);

  // Spinner while the refreshes are in flight, the error message when the
  // round list failed (closing and reopening retries), otherwise the picker.
  const renderContent = () => {
    if (loading) {
      return (
        <div className="lbv2__modal-status">
          <div className="lbv2__loader">
            <Loader type="Oval" color={ '#333F48' } height={ 20 } width={ 12 }></Loader>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="lbv2__modal-status">
          <div className="lbv2__error">{roundsFailedMessage()}</div>
        </div>
      );
    }

    return showRoundsModal
      ? <RoundsModalContainer key={session} handleClickEvent={handleClickEvent} handleClickSummary={handleClickSummary} handleClickEmptyRound={handleClickEmptyRound} />
      : <EventsListModalContainer handleClickEvent={handleClickEvent} handleClickSummary={handleClickSummary} />;
  };

  return (
    <MobileModal ref={dialogRef} type='lbv2__events' showSearch={!isManagerSite}>
      {renderContent()}
    </MobileModal>
  );
});

MobilePicker.propTypes = {
  viewMode: PropTypes.oneOf(Object.values(VIEW_MODES)),
  isManagerSite: PropTypes.bool,
  showRoundsModal: PropTypes.bool,
  fetchEvents: PropTypes.func.isRequired,
  fetchRounds: PropTypes.func.isRequired,
  handleClickEvent: PropTypes.func.isRequired,
  handleClickSummary: PropTypes.func.isRequired,
  handleClickEmptyRound: PropTypes.func.isRequired,
};

export default MobilePicker;
